import React, { useMemo } from "react";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { IconButton, Tooltip } from "@mui/material";

// Components
import Author from "components/Table/Author";
import CustomCheckbox from "components/CustomCheckbox/CustomCheckbox";
import FontComponent from "components/Responsive/fonts";
import pxToRem from "assets/theme/functions/pxToRem";

// Constants
import Constants, { Icons, Common, Colors, defaultData } from "utils/Constants";

// 3rd Party Libraries
import moment from "moment";

// Shared with the Equipment Location modal so both hovers look and read the same.
import { darkTooltipProps, buildQuantityLines } from "layouts/resources/locationTooltip";

// Tints the Certificate Expiry cell by how close the date is: red once expired, orange within
// 30 days, yellow within 60. `forColor` picks the cell background or the text colour.
function checkCertificateExpiry(expiryDate, forColor) {
  const daysRemaining = moment(expiryDate).diff(moment(), "days");

  if (daysRemaining <= 0) return forColor === "background" ? Colors.LIGHT_RED : Colors.DARK_RED;
  if (daysRemaining <= 30) {
    return forColor === "background" ? Colors.LIGHT_ORANGE : Colors.DARK_ORANGE;
  }
  if (daysRemaining <= 60) {
    return forColor === "background" ? Colors.LIGHT_YELLOW : Colors.DARK_YELLOW;
  }
  return "";
}

export default function ProductData({
  productList = [],
  handleEditDrawerOpen = () => {},
  handleOpenEquipmentDetailDrawer = () => {},
  permissions = {},
  stockShow = false,
  updateStockCheckboxFunc = () => {},
  stockData = [],
  handleOpenEquipmentLocation = () => {},
  handleOpenPermanentType,
}) {
  const filteredList = useMemo(
    () => productList,
    [productList, stockShow] // Dependencies
  );

  // FontComponent reads media queries, so it has to run in the component body rather than
  // inside the row map. Matches the size <Author /> renders at.
  const cellFontSize = FontComponent({ sizes: {} });

  const rows = useMemo(() => {
    if (!filteredList) return [];
    return filteredList.map((element, index) => {
      // One row per equipment; every warehouse slot it occupies is folded into the Location QR
      // cell. Records that predate the junction collection fall back to the equipment's own QR.
      const locationSlots = (element?.warehouseInfo || [])
        .filter((slot) => slot?.locationQRNumber)
        .map((slot) => ({ qr: slot.locationQRNumber, quantity: slot.quantity }));

      if (locationSlots.length === 0 && element?.locationQRNumber) {
        locationSlots.push({ qr: element.locationQRNumber, quantity: element.quantity });
      }

      // The cell shows codes only — the hover carries how much sits in each slot.
      const locationQRNumbers = locationSlots.map((slot) => slot.qr);
      const locationSlotTitle = buildQuantityLines(
        locationSlots.map((slot) => ({ label: slot.qr, quantity: slot.quantity }))
      );

      // Every place this equipment currently sits — warehouses first, then the projects it has
      // been dispatched to. Server-built so this cell and the Equipment Location modal always
      // name the same places.
      const locationSummary = element?.locationSummary || [];
      const locationNames = locationSummary.map((place) => place.label);
      const locationSummaryTitle = buildQuantityLines(locationSummary);

      // Under-allocation is permitted at registration, so stock on hand can exceed what has
      // actually been assigned a slot. Report both rather than hiding the unracked remainder.
      const totalQuantity = element?.quantity;
      const rackedQuantity = (element?.warehouseInfo || []).reduce(
        (sum, slot) => sum + (Number(slot?.quantity) || 0),
        0
      );
      const hasUnrackedStock =
        (element?.warehouseInfo || []).length > 0 && rackedQuantity !== Number(totalQuantity);

      // The nearest expiry across the equipment's certificates, ignoring ones with no end date.
      const certificateExpiryDate =
        (element?.certificateType || [])
          .filter((certificate) => certificate?.endDate)
          .map((certificate) => new Date(certificate.endDate))
          .sort((a, b) => a - b)[0]
          ?.toISOString() ?? null;

      return {
        stockCheck: element?.equipmentType?.quantityType?.[0]?.quantityType !== Common.UNIQUE && (
          <CustomCheckbox
            name="check"
            checked={stockData?.some(
              (item) => item?.[Constants.MONGOOSE_ID] === element?.[Constants.MONGOOSE_ID]
            )}
            onChange={(e) => updateStockCheckboxFunc(element, e.target.checked)}
          />
        ),
        srNo: <Author name={index + 1} />,
        equipmentcategory: (
          <Author
            name={
              element?.equipmentType?.equipmentCategory[0]?.name ||
              element?.equipmentType?.equipmentCategory?.name
            }
          />
        ),
        equipmenttype: <Author name={element?.equipmentType?.type} />,
        productNumber: (
          <Author
            name={
              element?.equipmentNumber && element?.equipmentNumber !== null
                ? element?.equipmentNumber
                : Constants.NA
            }
          />
        ),
        equipmentname: (
          <MDBox display="flex" justifyContent="start" alignItems="center">
            <img
              src={
                element?.equipmentImage?.[0]?.url
                  ? element?.equipmentImage?.[0]?.url
                  : process.env.REACT_APP_IMAGE_NOT_FOUND
              }
              alt={element?.equipmentImage}
              key={element?.equipmentImage?.[0]?.name}
              style={{
                width: pxToRem(50),
                height: pxToRem(50),
                marginRight: pxToRem(10),
                borderRadius: "8px",
              }}
            />
            <MDBox
              style={{
                flex: 1,
                width: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <Author name={element?.name} />
            </MDBox>
          </MDBox>
        ),
        serialNumber: <Author name={element?.serialNumber ?? Constants.NA} />,
        // Composite location: warehouseCode-{row}-{section}-{shelf}, one per warehouse slot.
        // Only the first is shown so every row stays the same height; the rest are on hover.
        locationQRNumber:
          locationQRNumbers.length === 0 ? (
            <Author name={Constants.NA} />
          ) : (
            <Tooltip
              {...darkTooltipProps}
              // Right of the cell rather than above it, so the tooltip never covers the
              // neighbouring rows in the same column.
              placement="right"
              // One QR per line. Plain text rather than nested boxes, so the tooltip's own
              // light-on-dark styling applies instead of being overridden to dark-on-dark.
              title={locationSlotTitle}
            >
              <MDBox display="flex" alignItems="center" sx={{ minWidth: 0 }}>
                {/*
                  Deliberately not <Author />: it attaches its own tooltip whenever the text is
                  ellipsised, which would fire alongside this one and put two tooltips on screen.
                  The code truncates here; the badge must never be what gets clipped.
                */}
                <MDTypography
                  variant="caption"
                  sx={{
                    fontSize: cellFontSize,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {locationQRNumbers[0]}
                </MDTypography>
                {locationQRNumbers.length > 1 && (
                  <MDTypography
                    variant="caption"
                    fontWeight="medium"
                    sx={{
                      ml: 0.75,
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                      color: Colors.PRIMARY,
                    }}
                  >
                    {`+${locationQRNumbers.length - 1} more`}
                  </MDTypography>
                )}
              </MDBox>
            </Tooltip>
          ),
        availableStocks: hasUnrackedStock ? (
          <Tooltip
            {...darkTooltipProps}
            placement="left"
            title={`${rackedQuantity} assigned to a location\n${totalQuantity} on hand\n${
              Number(totalQuantity) - rackedQuantity
            } not yet assigned a location`}
          >
            {/* Quantities carry no digit limit, so a long pair has to clip somewhere rather
                than run out of the column. Only the total gives way: the assigned count is
                the figure being read, and half a number ("3." from "3.42") is worse than no
                number. The exact figures are on the hover either way. */}
            <MDBox
              display="flex"
              alignItems="baseline"
              justifyContent="center"
              sx={{ minWidth: 0, maxWidth: "100%" }}
            >
              <MDTypography
                variant="caption"
                fontWeight="medium"
                sx={{ fontSize: cellFontSize, flexShrink: 0, whiteSpace: "nowrap" }}
              >
                {rackedQuantity}
              </MDTypography>
              <MDTypography
                variant="caption"
                sx={{
                  ml: 0.5,
                  fontSize: cellFontSize,
                  color: Colors.LIGHT_GRAY3,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {`/ ${totalQuantity}`}
              </MDTypography>
            </MDBox>
          </Tooltip>
        ) : (
          // Centred to match the `assigned / total` rows above; the column header is centred
          // and Author would otherwise left-align a lone figure.
          <Author name={totalQuantity} textAlign="center" />
        ),
        certificateExpiry: (
          <Author
            name={
              certificateExpiryDate
                ? moment(certificateExpiryDate).format(defaultData.WEB_DATE_FORMAT)
                : ""
            }
            cellColor={
              certificateExpiryDate
                ? checkCertificateExpiry(certificateExpiryDate, "background")
                : ""
            }
            style={{
              color: certificateExpiryDate
                ? checkCertificateExpiry(certificateExpiryDate, "color")
                : "",
              fontWeight: "bold",
            }}
          />
        ),
        equipmentLocation:
          element?.equipmentType?.quantityType?.[0]?.quantityType === Common.UNIQUE ? (
            <Author name={element?.inventoryLocation || Constants.NA} />
          ) : (
            // Same shape as the Location QR cell: first place, a "+N more" badge for the rest,
            // and the full split on hover. The info icon keeps its own tooltip and stays the
            // way into the modal — two separate hover targets rather than nested tooltips.
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              sx={{ minWidth: 0 }}
            >
              {locationNames.length > 0 && (
                <Tooltip {...darkTooltipProps} placement="left" title={locationSummaryTitle}>
                  <MDBox display="flex" alignItems="center" sx={{ minWidth: 0 }}>
                    <MDTypography
                      variant="caption"
                      sx={{
                        fontSize: cellFontSize,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {locationNames[0]}
                    </MDTypography>
                    {locationNames.length > 1 && (
                      <MDTypography
                        variant="caption"
                        fontWeight="medium"
                        sx={{
                          ml: 0.75,
                          flexShrink: 0,
                          whiteSpace: "nowrap",
                          color: Colors.PRIMARY,
                        }}
                      >
                        {`+${locationNames.length - 1} more`}
                      </MDTypography>
                    )}
                  </MDBox>
                </Tooltip>
              )}
              <Tooltip title="View Equipment Location" placement="top">
                <IconButton
                  aria-label="fingerprint"
                  color="info"
                  onClick={() =>
                    handleOpenEquipmentLocation({
                      id: element?.[Constants.MONGOOSE_ID],
                      name: element?.name,
                      quantity: element?.quantity,
                    })
                  }
                >
                  {Icons.INFO}
                </IconButton>
              </Tooltip>
            </MDBox>
          ),
        action: (
          <MDBox>
            <IconButton
              aria-label="fingerprint"
              color="info"
              onClick={() => handleOpenEquipmentDetailDrawer(element?.[Constants.MONGOOSE_ID])}
            >
              {Icons.VIEW}
            </IconButton>
            {permissions?.update && (
              <IconButton
                aria-label="fingerprint"
                color="error"
                onClick={() => handleEditDrawerOpen(element?.[Constants.MONGOOSE_ID])}
              >
                {Icons.EDIT}
              </IconButton>
            )}
            {permissions?.update && element?.equipmentType?.isTemporary && (
              <IconButton
                aria-label="fingerprint"
                color="error"
                onClick={() => handleOpenPermanentType(element)}
              >
                {Icons.MOVE}
              </IconButton>
            )}
          </MDBox>
        ),
      };
    });
  }, [filteredList, permissions, stockData]); // Dependencies

  const columns = [
    ...(stockShow ? [{ Header: "", accessor: "stockCheck", align: "left" }] : []),
    { Header: "No.", accessor: "srNo", width: "2%" },
    { Header: "Equipment Category", accessor: "equipmentcategory", align: "left", width: "12%" },
    { Header: "Equipment Type", accessor: "equipmenttype", align: "left" },
    { Header: "Product Number", accessor: "productNumber", align: "left" },
    {
      Header: "Equipment Name",
      accessor: "equipmentname",
      align: "left",
      width: "18%",
    },
    { Header: "Serial Number", accessor: "serialNumber", align: "left" },
    {
      Header: "Location QR",
      accessor: "locationQRNumber",
      align: "left",
      // Composite codes run longer than the table's default 200px clamp.
      cellMaxWidth: "300px",
      width: "16%",
    },
    { Header: "Available Stocks", accessor: "availableStocks", align: "center" },
    { Header: "Certificate Expiry", accessor: "certificateExpiry", align: "left" },
    { Header: "Location", accessor: "equipmentLocation", align: "left" },
    { Header: "Action", accessor: "action", align: "center" },
  ];

  const tableData = {
    columns,
    rows,
  };
  return tableData;
}
